/**
 * L'unique fichier qui parle au WebAssembly.
 *
 * Tout le reste de l'interface passe par ici. La contrainte n'est pas
 * cosmétique : le jour où la signature d'un appel change côté Rust, un seul
 * fichier casse, et il casse à la compilation.
 *
 * Ce module ne contient **aucune règle**. S'il se met un jour à décider quoi
 * que ce soit — un plafond, un cumul, un arrondi — c'est une règle que le
 * serveur ne partage pas, et les deux calculeront différemment (ADR-003).
 */

import init, {
  ajouter_affliction,
  renommer_affliction as renommer_affliction_wasm,
  reprendre_un_objet_libre as reprendre_un_objet_libre_wasm,
  renommer_un_objet_libre as renommer_un_objet_libre_wasm,
  annoter_un_objet_libre as annoter_un_objet_libre_wasm,
  corriger_un_chiffre_d_affliction as corriger_un_chiffre_d_affliction_wasm,
  corriger_un_chiffre_d_objet as corriger_un_chiffre_d_objet_wasm,
  apercu_des_jets as apercu_des_jets_wasm,
  chiffrer_une_affliction as chiffrer_une_affliction_wasm,
  effacer_un_chiffre_d_affliction as effacer_un_chiffre_d_affliction_wasm,
  chiffrer_un_objet_libre as chiffrer_un_objet_libre_wasm,
  effacer_un_chiffre_d_objet as effacer_un_chiffre_d_objet_wasm,
  ajuster_compteur,
  acheter_une_augmentation,
  acheter_une_capacite,
  acheter_une_maitrise,
  prouesse as prouesse_du_personnage,
  domaines as domaines_du_personnage,
  machines as machines_du_personnage,
  mains_de_dieu as mains_du_personnage,
  vie_sauvage as vie_sauvage_du_personnage,
  alchimie as alchimie_du_personnage,
  ecrire_un_rituel as ecrire_un_rituel_wasm,
  effacer_un_rituel as effacer_un_rituel_wasm,
  magie as magie_de_la_carte,
  magie_du_personnage as magie_du_personnage_wasm,
  grimoire as grimoire_wasm,
  designer_un_favori as designer_un_favori_wasm,
  choisir_un_element as choisir_un_element_wasm,
  jouer_une_carte as jouer_une_carte_wasm,
  racheter_une_carte as racheter_une_carte_wasm,
  nouvelle_journee as nouvelle_journee_wasm,
  apprivoiser as apprivoiser_wasm,
  instruire_le_compagnon as instruire_le_compagnon_wasm,
  acheter_une_main as acheter_une_main_wasm,
  forger_une_main as forger_une_main_wasm,
  forger_une_main_libre as forger_une_main_libre_wasm,
  renommer_une_main as renommer_une_main_wasm,
  charger_une_main as charger_une_main_wasm,
  defaire_une_main as defaire_une_main_wasm,
  ajouter_une_machine as ajouter_une_machine_wasm,
  retirer_une_machine as retirer_une_machine_wasm,
  renommer_une_machine as renommer_une_machine_wasm,
  abimer_une_machine as abimer_une_machine_wasm,
  basculer_equipement_de_machine as basculer_equipement_de_machine_wasm,
  basculer_greffe as basculer_greffe_wasm,
  promouvoir as promouvoir_wasm,
  basculer_narratif as basculer_narratif_wasm,
  accomplir_le_sacrement as accomplir_le_sacrement_wasm,
  ajouter_objet,
  ajouter_objet_libre,
  renforts_possibles as renforts_d_un_objet,
  attribuer_une_propriete as attribuer_une_propriete_wasm,
  tenter_une_amelioration as tenter_une_amelioration_wasm,
  basculer_amelioration as basculer_amelioration_wasm,
  ajuster_la_bourse as ajuster_la_bourse_wasm,
  materiel_d_amelioration as materiel_d_amelioration_wasm,
  poser_un_renfort as poser_un_renfort_wasm,
  apercu_encaissement,
  progression as progression_du_personnage,
  articles_du_commerce,
  assistant_de_jet,
  ranger_objet as ranger_objet_wasm,
  equipements_abimes,
  retirer_objet,
  sac as sac_du_personnage,
  materiel_de_reparation,
  subir_un_coup,
  tenter_une_reparation,
  basculer_blessure,
  blesser as blesser_wasm,
  compositions_chanceuses,
  opposer_un_jet,
  adversaires_du_bestiaire,
  competences_d_un_adversaire,
  localisation_touchee,
  protections_du_personnage,
  basculer_folie,
  basculer_statut,
  resoudre_jet,
  retirer_affliction,
  charger_referentiel,
  creer_personnage,
  etat_de_creation,
  fiche_de_personnage,
  referentiel_de_creation,
  region_tiree,
  tirage_de_capacite,
} from './wasm/rules.js'

// ---------------------------------------------------------------------------
// Ce que le référentiel envoie
// ---------------------------------------------------------------------------

export interface Region {
  id: string
  nom: string
  resume: string
  min: number
  max: number
  caracteristiques_relevees: string[]
  releve_de: number
  options: string[]
  a_retenir: number
  /**
   * La quatrième entrée de la liste du livre : « une Capacité tirée au
   * hasard ». Elle prend l'une des `a_retenir` places, elle ne s'y ajoute pas.
   */
  tirage_en_option: boolean
}

export interface Classe {
  id: string
  nom: string
  resume: string
  faction: string | null
  caracteristiques_relevees: string[]
  releve_de: number
  competences_octroyees: Record<string, number>
  capacites: string[]
}

export interface Caracteristique {
  id: string
  /** L'abréviation à trois lettres de la fiche officielle. */
  code: string
  nom: string
}

export interface Competence {
  id: string
  nom: string
  carac: string
  avancee: boolean
  valeur_de_depart: number | null
}

export interface Capacite {
  nom: string
  resume: string
}

export interface Budget {
  valeur_de_depart: number
  augmentations: number
  plafond_a_la_creation: number
  plafond_absolu: number
}

export interface Bareme {
  caracteristiques: Budget
  competences: Budget
  chance: number
  maitrises_au_choix: number
  maitrises_offertes: string[]
}

export interface Etape {
  id: string
  nom: string
  page: number
}

export interface Derivee {
  id: string
  nom: string
  /** L'abréviation de la fiche officielle — « PV », « FF », « PS ». */
  abrev: string
}

export interface Referentiel {
  regions: Region[]
  classes: Classe[]
  caracteristiques: Caracteristique[]
  competences: Competence[]
  derivees: Derivee[]
  capacites: Record<string, Capacite>
  categories_armes: Record<string, string>
  bareme: Bareme
  etapes: Etape[]
}

// ---------------------------------------------------------------------------
// La feuille, et ce que le domaine en dit
// ---------------------------------------------------------------------------

export interface CapaciteChoisie {
  capacite: string
  parametre?: string | null
  /** `null` quand la Capacité vient de la Région ; le D100 quand elle est tirée. */
  d100?: number | null
}

export interface Arbitrage {
  noeud: string
  index: number
  option?: number
  maitrises?: string[]
  objets?: string[]
}

export interface Feuille {
  nom: string
  region: string | null
  classe: string | null
  caracteristiques: Record<string, number>
  competences: Record<string, number>
  capacites: CapaciteChoisie[]
  maitrises: string[]
  dotations: Arbitrage[]
}

export interface Solde {
  budget: number
  depense: number
  reste: number
}

export interface SoldeDeChoix {
  attendus: number
  faits: number
  /** Combien des choix attendus peuvent venir d'un tirage. Jamais plus d'un. */
  tirages_possibles: number
  tirages_faits: number
  complet: boolean
}

export interface ChoixOuvert {
  noeud: string
  index: number
  forme: 'alternative' | 'maitrise' | 'amelioration_de_maitrise' | 'catalogue' | 'budget'
  nombre: number
  budget_livres: number | null
  catalogue: string | null
  options: string[]
}

export interface LigneDInventaire {
  id: string
  /** `null` quand aucun catalogue ne porte cet identifiant. */
  nom: string | null
  quantite: number
  source: string
}

export interface Anomalie {
  /** L'étape à laquelle elle se corrige, pour la ranger au bon endroit. */
  etape: string
  message: string
}

export interface Etat {
  caracteristiques: Solde
  competences: Solde
  capacites: SoldeDeChoix
  maitrises: SoldeDeChoix
  capacites_offertes: string[]
  maitrises_offertes: string[]
  competences_octroyees: Record<string, number>
  plafonds: Record<string, number>
  plafond_de_competence: number
  dotations_ouvertes: ChoixOuvert[]
  inventaire: LigneDInventaire[]
  a_arbitrer: string[]
  argent: number | null
  apercu: {
    competences: Record<string, number>
    derivees: Record<string, number>
  }
  anomalies: Anomalie[]
  creable: boolean
}

// ---------------------------------------------------------------------------
// Le personnage abouti
// ---------------------------------------------------------------------------

/**
 * Ce qui s'enregistre : des valeurs brutes, et rien de dérivé.
 *
 * Pas un point de vie, pas une Valeur réelle. Le jour où une formule change,
 * les personnages enregistrés se recalculent justes.
 */
export interface PersonnageEnregistre {
  nom: string
  region: string | null
  /** La pile de Classes, dans l'ordre d'entrée ; la dernière est la courante. */
  classes: string[]
  caracteristiques: Record<string, number>
  competences_brutes: Record<string, number>
  capacites: { capacite: string; parametre: string | null }[]
  maitrises: Record<string, number>
  inventaire: {
    id: string
    quantite: number
    source: string
    /** Ce que le joueur a écrit lui-même, quand aucun catalogue ne le porte. */
    libre?: { nom: string; encombrement: number; prix: number | null; note: string | null } | null
  }[]
  argent: number | null
  /** `null` sur une fiche enregistrée avant les compteurs : elle s'ouvre au complet. */
  compteurs: Compteurs | null
  /** Les Statuts subis en ce moment, par identifiant de catalogue. */
  statuts: string[]
  blessures: { localisation: string; severite: string }[]
  afflictions: { folie: string | null; libelle: string }[]
  /** Ce que la Prouesse a greffé, par identifiant. */
  prouesse: string[]
  /** Les Mains de Dieu forgées — celles du livre, celles de la table. */
  mains_de_dieu: unknown[]
  /** Le Compagnon du Rôdeur. `null` : il n'en a pas apprivoisé. */
  compagnon: unknown | null
  /** Les Rituels que la table a écrits. */
  rituels: unknown[]
  /** Les Drones et MEKA que le personnage pilote. */
  machines: {
    modele: string
    nom: string | null
    degats_subis: number
    equipements: string[]
  }[]
  /** Les nœuds de Rang franchis. Vide : le Rang 1 de la Classe, déduit. */
  rangs: string[]
  /** Les Prérequis narratifs que la table a validés, texte du livre. */
  narratifs_valides: string[]
  a_arbitrer: string[]
}

/** Ce qui bouge en séance, et rien d'autre. */
export interface Compteurs {
  /** Signés : entre 0 et −5, le personnage est en État critique mais vivant. */
  pv: number
  chance: number
  stress: number
  contamination: number
  xp_total: number
  xp_depense: number
  pi_cumules: number
  pi_disponibles: number
}

/** Ce qu'un ajustement vise. */
export type Compteur =
  | 'points_de_vie'
  | 'chance'
  | 'stress'
  | 'contamination'
  /** L'XP gagné. Un delta négatif corrige une saisie, il ne dépense pas. */
  | 'experience'
  /** L'XP dépensé — la colonne qu'un achat alimente. */
  | 'experience_depensee'
  /** Les PI cumulés, sur lesquels se lit la notoriété. */
  | 'influence'
  /** Le solde de PI seul : c'est lui qu'une dépense entame. */
  | 'influence_disponible'

export interface JaugeBornee {
  valeur: number
  min: number
  /** Le maximum du personnage tel qu'il est aujourd'hui. */
  max: number
  /** Celui qu'il atteindrait en poussant au bout la Compétence qui le porte. */
  theorique: number
}

export interface CompteursVue {
  pv: JaugeBornee
  chance: JaugeBornee
  stress: JaugeBornee
  contamination: JaugeBornee
  xp_total: number
  xp_depense: number
  xp_disponible: number
  pi_cumules: number
  pi_disponibles: number
  /** Le Niveau de notoriété, lu sur les PI cumulés. */
  notoriete: string | null
  etat_vital: 'indemne' | 'critique' | 'mort'
  /** Ce que la situation impose de faire, dit en clair. */
  alertes: string[]
}

/** Le verdict de la Création. Le refus est une valeur, pas une exception. */
export type Verdict =
  | { verdict: 'cree'; personnage: PersonnageEnregistre }
  | { verdict: 'refuse'; anomalies: Anomalie[] }

export interface LigneDeCarac {
  id: string
  /** L'abréviation à trois lettres de la fiche officielle. */
  code: string
  nom: string
  valeur: number
}

export interface LigneDeCompetence {
  id: string
  nom: string
  carac: string
  avancee: boolean
  brute: number
  /** Brute + Caractéristique : c'est elle qu'on teste. */
  reelle: number
}

export interface LigneDeDerivee {
  id: string
  nom: string
  abrev: string
  valeur: number
}

export interface LigneDeCapacite {
  id: string
  nom: string
  resume: string
  parametre: string | null
  /** `commune`, `classe` — ou `inconnue` si aucun catalogue ne la porte. */
  source: string
}

export interface LigneDeStatut {
  id: string
  nom: string
  resume: string
  /** `normale`, `esquiveseule` ou `aucune` : ce qu'il laisse faire en défense. */
  reaction_defensive: string
  /** Vrai quand la fiche l'impose — l'État critique ne se déclare pas. */
  automatique: boolean
}

export interface LigneDeBlessure {
  localisation: string
  nom: string
  /** `attaque` ou `attaque_critique`. */
  severite: string
  resume: string
}

/** Ce qu'une Affliction ou un objet inventé coûte, en chiffres. */
export interface ModificateurChiffre {
  /** Sa place dans la liste : c'est par là qu'on l'efface. */
  position: number
  /** « Séduction », « Compétences de Charisme », « tous les Tests ». */
  dit: string
  valeur: number
}

/** Ce qu'un modificateur écrit par la table peut viser. */
export type CibleEcrite = 'tous' | 'famille' | 'competence' | 'caracteristique'

export interface LigneDAffliction {
  /** Sa place dans la liste : c'est par là qu'on la retire. */
  index: number
  /** Ce qu'elle coûte, en chiffres. Vide tant que la table n'a rien chiffré. */
  modificateurs: ModificateurChiffre[]
  nom: string
  effet: string
  libelle: string
  du_livre: boolean
  incurable: boolean
}

/**
 * Chiffre une Affliction écrite par la table.
 *
 * Jusqu'ici les chiffres vivaient dans le libellé, en prose — « Brûler −10 CHA
 * −30 Séduction » — et aucun n'atteignait un jet. Typés, ils comptent.
 */
/** Corrige le libellé d'une Affliction écrite par la table. Ses chiffres restent. */
export function renommerAffliction(
  personnage: PersonnageEnregistre,
  index: number,
  libelle: string,
): PersonnageEnregistre {
  return renommer_affliction_wasm(personnage, index, libelle) as PersonnageEnregistre
}

/** Reprend un objet déjà écrit à la main : un de plus sur la pile. */
export function reprendreUnObjetLibre(
  personnage: PersonnageEnregistre,
  nom: string,
): PersonnageEnregistre {
  return reprendre_un_objet_libre_wasm(personnage, nom) as PersonnageEnregistre
}

/** Corrige la valeur d'un chiffre posé sur une Affliction. Zéro est refusé. */
export function corrigerUnChiffreDAffliction(
  personnage: PersonnageEnregistre,
  index: number,
  position: number,
  valeur: number,
): PersonnageEnregistre {
  return corriger_un_chiffre_d_affliction_wasm(
    personnage,
    index,
    position,
    valeur,
  ) as PersonnageEnregistre
}

/** Corrige la valeur d'un chiffre posé sur un objet écrit à la main. */
export function corrigerUnChiffreDObjet(
  personnage: PersonnageEnregistre,
  objet: string,
  position: number,
  valeur: number,
): PersonnageEnregistre {
  return corriger_un_chiffre_d_objet_wasm(
    personnage,
    objet,
    position,
    valeur,
  ) as PersonnageEnregistre
}

/** Corrige le nom d'un objet écrit à la main. Un objet du livre ne bouge pas. */
export function renommerObjetLibre(
  personnage: PersonnageEnregistre,
  objet: string,
  nom: string,
): PersonnageEnregistre {
  return renommer_un_objet_libre_wasm(personnage, objet, nom) as PersonnageEnregistre
}

/** Change la note d'un objet écrit à la main. Vidée, elle s'efface. */
export function annoterObjetLibre(
  personnage: PersonnageEnregistre,
  objet: string,
  note: string,
): PersonnageEnregistre {
  return annoter_un_objet_libre_wasm(personnage, objet, note) as PersonnageEnregistre
}

export function chiffrerUneAffliction(
  personnage: PersonnageEnregistre,
  index: number,
  cible: CibleEcrite,
  id: string,
  valeur: number,
): PersonnageEnregistre {
  return chiffrer_une_affliction_wasm(personnage, index, cible, id, valeur) as PersonnageEnregistre
}

export function effacerUnChiffreDAffliction(
  personnage: PersonnageEnregistre,
  index: number,
  position: number,
): PersonnageEnregistre {
  return effacer_un_chiffre_d_affliction_wasm(personnage, index, position) as PersonnageEnregistre
}

/** Chiffre un objet inventé à la table. Il ne pèse sur un jet que porté. */
export function chiffrerUnObjetLibre(
  personnage: PersonnageEnregistre,
  objet: string,
  cible: CibleEcrite,
  id: string,
  valeur: number,
): PersonnageEnregistre {
  return chiffrer_un_objet_libre_wasm(personnage, objet, cible, id, valeur) as PersonnageEnregistre
}

export function effacerUnChiffreDObjet(
  personnage: PersonnageEnregistre,
  objet: string,
  position: number,
): PersonnageEnregistre {
  return effacer_un_chiffre_d_objet_wasm(personnage, objet, position) as PersonnageEnregistre
}

export interface LigneDeLocalisation {
  id: string
  nom: string
}

export interface LigneDeFolie {
  id: string
  nom: string
  effet: string
}

export interface LigneDeMaitrise {
  categorie: string
  nom: string
  niveau: number
}

/** La fiche complète, résolue en un seul appel (ADR-017). */
export interface Fiche {
  nom: string
  region: string | null
  classe: string | null
  faction: string | null
  caracteristiques: LigneDeCarac[]
  competences: LigneDeCompetence[]
  derivees: LigneDeDerivee[]
  chance: number
  capacites: LigneDeCapacite[]
  maitrises: LigneDeMaitrise[]
  statuts: LigneDeStatut[]
  /** Les douze Statuts du livre, pour en poser un. */
  statuts_offerts: LigneDeStatut[]
  blessures: LigneDeBlessure[]
  localisations: LigneDeLocalisation[]
  afflictions: LigneDAffliction[]
  folies_offertes: LigneDeFolie[]
  inventaire: LigneDInventaire[]
  argent: number | null
  compteurs: CompteursVue
  a_arbitrer: string[]
}

export interface Tirage {
  verdict: 'retenue' | 'a_relancer' | 'hors_table'
  capacite: string | null
  d100: number
}

// ---------------------------------------------------------------------------
// Le pont
// ---------------------------------------------------------------------------

let pret = false

/**
 * Charge le module et le référentiel qu'il embarque.
 *
 * Rend `null` si tout va bien, ou le message d'erreur à afficher. On ne lève
 * pas : un référentiel invalide est une information que l'utilisateur doit
 * voir, pas une page blanche.
 */
export async function chargerMoteur(): Promise<string | null> {
  if (pret) return null
  try {
    await init()
    charger_referentiel()
    pret = true
    return null
  } catch (e) {
    return e instanceof Error ? e.message : String(e)
  }
}

export function referentiel(): Referentiel {
  return referentiel_de_creation() as Referentiel
}

export function etat(feuille: Feuille): Etat {
  return etat_de_creation(feuille) as Etat
}

export function regionTiree(d100: number): string | undefined {
  return region_tiree(d100) ?? undefined
}

export function tirerCapacite(feuille: Feuille, d100: number): Tirage {
  return tirage_de_capacite(feuille, d100) as Tirage
}

/** Fait aboutir la feuille, ou rend la liste de ce qui l'en empêche. */
export function creerPersonnage(feuille: Feuille): Verdict {
  return creer_personnage(feuille) as Verdict
}

/** Tout ce qu'une fiche affiche, recalculé depuis les valeurs brutes. */
export function fiche(personnage: PersonnageEnregistre): Fiche {
  return fiche_de_personnage(personnage) as Fiche
}

/**
 * Applique un delta à un compteur et rend le personnage à réenregistrer.
 *
 * Un delta et non une valeur : c'est ce qui permettra plus tard au meneur de
 * retirer 5 Points de vie pendant que le joueur en régénère 2. Les bornes sont
 * tenues par le moteur.
 */
export function ajusterCompteur(
  personnage: PersonnageEnregistre,
  compteur: Compteur,
  delta: number,
): PersonnageEnregistre {
  return ajuster_compteur(personnage, compteur, delta) as PersonnageEnregistre
}

/** Pose ou retire un Statut, et rend le personnage à réenregistrer. */
export function basculerStatut(
  personnage: PersonnageEnregistre,
  statut: string,
): PersonnageEnregistre {
  return basculer_statut(personnage, statut) as PersonnageEnregistre
}

/** Ouvre ou referme une blessure. Une Localisation n'en porte qu'une. */
export function basculerBlessure(
  personnage: PersonnageEnregistre,
  localisation: string,
  severite: string,
): PersonnageEnregistre {
  return basculer_blessure(personnage, localisation, severite) as PersonnageEnregistre
}

/** Pose ou retire une Folie du catalogue. */
export function basculerFolie(
  personnage: PersonnageEnregistre,
  folie: string,
): PersonnageEnregistre {
  return basculer_folie(personnage, folie) as PersonnageEnregistre
}

/** Ajoute une Affliction que le livre ne catalogue pas — une Phobie, souvent. */
export function ajouterAffliction(
  personnage: PersonnageEnregistre,
  libelle: string,
): PersonnageEnregistre {
  return ajouter_affliction(personnage, libelle) as PersonnageEnregistre
}

export function retirerAffliction(
  personnage: PersonnageEnregistre,
  index: number,
): PersonnageEnregistre {
  return retirer_affliction(personnage, index) as PersonnageEnregistre
}

// ---------------------------------------------------------------------------
// Encaisser un coup
// ---------------------------------------------------------------------------

/** Le détail d'un coup reçu, terme par terme. */
export interface Encaissement {
  localisation: string
  localisation_nom: string
  /** Les Dégâts annoncés par le meneur, avant déduction. */
  bruts: number
  resistance_physique: number
  protection: number
  /** La pièce d'armure qui a encaissé, quand il y en a une. */
  piece: string | null
  /** Ce que le personnage perd vraiment. */
  subis: number
}

// ---------------------------------------------------------------------------
// La progression
// ---------------------------------------------------------------------------

/** Une Augmentation qu'on peut s'offrir, ou pas encore. */
export interface Augmentation {
  id: string
  nom: string
  /** Brute pour une Compétence, la Valeur pour une Caractéristique. */
  actuelle: number
  /** Ce que coûte le point suivant. `null` au plafond. */
  cout: number | null
  abordable: boolean
  /** La Valeur réelle d'aujourd'hui et celle qu'on obtiendrait : c'est le
      nombre qu'on jette, donc le seul qui dise ce que l'achat change. */
  reelle: number | null
  reelle_apres: number | null
  /** La Caractéristique dont la Compétence dépend, pour grouper la liste. */
  carac: string | null
  /** Pour une Caractéristique, le nombre de Compétences qu'elle porte. */
  competences_portees: number
}

/** Une Maîtrise, et ce que coûterait la marche suivante. */
export interface MaitriseAchetable {
  categorie: string
  nom: string
  niveau: number
  prochain: number | null
  cout: number | null
  abordable: boolean
  /** Ce que le meneur doit valider en plus de l'XP. */
  condition: string | null
}

/** Une Capacité qu'on pourrait apprendre, et ce qui s'y oppose. */
export interface CapaciteAchetable {
  id: string
  nom: string
  resume: string
  /** `null` pour la seule Capacité dont le livre imprime « / » au lieu d'un prix. */
  cout: number | null
  abordable: boolean
  /** Ce qui manque encore, en toutes lettres — le narratif à part. */
  manques: string[]
  /** Ce que seule la table peut trancher : une case à cocher. */
  narratifs: Narratif[]
  accessible: boolean
}

/** Un Prérequis que la fiche ne peut pas vérifier : la table le coche. */
export interface Narratif {
  texte: string
  valide: boolean
}

/** La cérémonie qui ouvre un Rang, et ce qu'elle rapporte. */
export interface Sacrement {
  id: string
  nom: string
  page: number
  /** Ce que la cérémonie demande, paraphrasé du livre. */
  resume: string
  recompense: number
  /** `xp` ou `pi` : l'Ascension paie en Points d'Influence. */
  unite: string
  /** Le Prérequis narratif qu'il satisfait, mot pour mot. */
  exige: string
  accompli: boolean
}

/** Un Rang de la Classe : celui qu'on tient, ou celui qu'on vise. */
export interface Rang {
  id: string
  titre: string
  rang: number
  /** L'intitulé qui coiffe plusieurs nœuds d'un même Rang, s'il y en a un. */
  groupe: string | null
  page: number
  /** Ce qui manque, en toutes lettres — le narratif à part, plus bas. */
  manques: string[]
  narratifs: Narratif[]
  /** La cérémonie qui ouvre ce Rang, quand le livre en prévoit une. */
  sacrement: Sacrement | null
  accessible: boolean
  /** Ce que la promotion apporte, annoncé avant qu'on la prenne. */
  competences: string[]
  capacites: string[]
  dotations: string[]
  argent: number | null
}

export interface Progression {
  xp_total: number
  xp_depense: number
  xp_disponible: number
  caracteristiques: Augmentation[]
  competences: Augmentation[]
  maitrises: MaitriseAchetable[]
  capacites: CapaciteAchetable[]
  /** Le Rang tenu aujourd'hui. `null` sans Classe. */
  rang_actuel: Rang | null
  /** Les Rangs immédiatement suivants, et ce qui s'oppose à chacun. */
  rangs: Rang[]
}

/**
 * Ce que le personnage peut s'offrir, et ce que coûte chaque marche.
 *
 * Le barème du Codex II p.39 se lit sur la valeur **visée** : c'est le point
 * qu'on s'apprête à gagner qui fixe le prix, pas celui qu'on a déjà.
 */
export function progression(personnage: PersonnageEnregistre): Progression {
  return progression_du_personnage(personnage) as Progression
}

/**
 * Apprend une Capacité commune.
 *
 * Le moteur vérifie les Prérequis avant de débiter quoi que ce soit : un
 * Prérequis narratif — « avoir été anobli » — bloque l'achat, parce que seule
 * la table peut dire qu'il est rempli.
 */
export function acheterUneCapacite(
  personnage: PersonnageEnregistre,
  capacite: string,
): PersonnageEnregistre {
  return acheter_une_capacite(personnage, capacite) as PersonnageEnregistre
}

/**
 * Franchit un Rang.
 *
 * Le livre veut une demande à la Faction et une cérémonie que le meneur mène
 * (Codex IV p.63) : l'application n'en tient pas lieu, elle enregistre ce qui
 * en résulte. Les Compétences avancées viennent d'office ; les Capacités du
 * nouveau Rang, elles, passent en vente dans la liste des achats.
 */
export function promouvoir(
  personnage: PersonnageEnregistre,
  noeud: string,
): PersonnageEnregistre {
  return promouvoir_wasm(personnage, noeud) as PersonnageEnregistre
}

/**
 * Accomplit une cérémonie de Rang, ou revient dessus.
 *
 * Un seul geste, comme dans le livre : le Prérequis narratif est satisfait et
 * la récompense entre au compteur — 500 XP pour le Premier Sacrement, 50 Points
 * d'Influence pour l'Ascension.
 */
export function accomplirLeSacrement(
  personnage: PersonnageEnregistre,
  sacrement: string,
): PersonnageEnregistre {
  return accomplir_le_sacrement_wasm(personnage, sacrement) as PersonnageEnregistre
}

/**
 * Coche, ou décoche, un Prérequis narratif tranché à la table.
 *
 * « Avoir fait l'objet du Premier Sacrement » ne se lit sur aucune ligne de la
 * fiche : c'est le meneur qui le dit, et la fiche s'en souvient.
 */
export function basculerNarratif(
  personnage: PersonnageEnregistre,
  texte: string,
): PersonnageEnregistre {
  return basculer_narratif_wasm(personnage, texte) as PersonnageEnregistre
}

/**
 * Acquiert ou améliore une Maîtrise.
 *
 * L'XP ne suffit pas : le livre exige aussi de l'entraînement ou des
 * Confrontations (Codex II p.38-39), et cela se tranche à la table.
 */
export function acheterUneMaitrise(
  personnage: PersonnageEnregistre,
  categorie: string,
): PersonnageEnregistre {
  return acheter_une_maitrise(personnage, categorie) as PersonnageEnregistre
}

/** Achète une Augmentation : la valeur monte, l'XP dépensée suit. */
export function acheterUneAugmentation(
  personnage: PersonnageEnregistre,
  quoi: 'caracteristique' | 'competence',
  id: string,
): PersonnageEnregistre {
  return acheter_une_augmentation(personnage, quoi, id) as PersonnageEnregistre
}

// ---------------------------------------------------------------------------
// Le sac
// ---------------------------------------------------------------------------

/**
 * Où se trouve un objet qu'on possède.
 *
 * Le livre ne nomme pas ces trois états mais les suppose partout : seule une
 * armure équipée protège, l'Encombrement compte ce qu'on trimballe, et
 * personne ne part en expédition avec tout ce qu'il possède.
 */
export type Rangement = 'equipe' | 'sac' | 'laisse'

/** Un trait d'un objet : son intitulé, et ce qu'il vaut. */
export interface TraitDObjet {
  /** « Dégâts », « Catégorie », « Protection », « Couvre ». */
  intitule: string
  /** Déjà mis en forme : « 10 + FF », « 3 », « Torse, Bras ». */
  valeur: string
  /** Changé par un Renforcement : l'écran le signale. */
  modifie: boolean
}

/** Une ligne du sac, avec tout ce que l'écran en dit. */
export interface LigneDeSac {
  id: string
  nom: string
  quantite: number
  /** `arme`, `armure`, `objet` — ou `libre` pour ce que le joueur a écrit. */
  genre: string
  source: string
  /** Déduit de `rangement` ; conservé pour les fiches d'avant les trois états. */
  porte: boolean
  rangement: Rangement
  /** Compte-t-il dans l'Encombrement ? Faux pour ce qu'on a laissé. */
  emporte: boolean
  /** Ce que le catalogue dit de cet objet-ci, prêt à mettre en page. */
  traits: TraitDObjet[]
  /**
   * Les Dégâts que cet exemplaire inflige, Force de frappe comprise.
   *
   * Le catalogue écrit « 6 + FF » ; ceci est le nombre. `null` hors d'une
   * arme, et pour les armes que le livre ne chiffre pas.
   */
  degats: number | null
  /** Ce que la ligne pèse en tout, quantité comprise. */
  encombrement: number
  prix: number | null
  /** La moitié du prix d'achat (Codex V p.150). */
  revente: number | null
  usure: string | null
  charges_restantes: number | null
  /** Les Renforcements posés sur cet exemplaire. */
  renforts: { source: string; sens: number; dit: string }[]
  /** Les Améliorations de Veredium montées dessus. */
  ameliorations: RenfortOffert[]
  /** Ce qu'ils changent : Dégâts pour une arme, protection pour une armure. */
  renfort: number
  /** Ce que le joueur a noté sur un objet écrit à la main. */
  note: string | null
}

export interface Sac {
  lignes: LigneDeSac[]
  argent: number | null
  portee: number
  maximum: number
  /** Dépasser l'Encombrement maximal expose au Statut Épuisé (Codex V p.135). */
  expose_a_epuise: boolean
}

/** Un objet du commerce, tel qu'on le choisit au ramassage. */
export interface Article {
  id: string
  nom: string
  genre: string
  encombrement: number
  prix: number | null
}

/** Tout ce que l'écran du Sac affiche, en un appel. */
export function sac(personnage: PersonnageEnregistre): Sac {
  return sac_du_personnage(personnage) as Sac
}

/** Tout ce qui peut entrer dans un sac — armes, armures, objets. */
export function articles(): Article[] {
  return articles_du_commerce() as Article[]
}

/** Ramasse un objet, ou l'achète en vidant d'autant la bourse. */
export function ajouterObjet(
  personnage: PersonnageEnregistre,
  objet: string,
  payer: boolean,
): PersonnageEnregistre {
  return ajouter_objet(personnage, objet, payer) as PersonnageEnregistre
}

/**
 * Inscrit au sac un objet qu'aucun catalogue ne porte.
 *
 * Le livre ne prétend pas tout lister, et une table invente : une lettre
 * scellée, un souvenir, un butin que le meneur improvise. Le nom, le poids et
 * le prix viennent du joueur — rien n'est deviné.
 */
export function ajouterObjetLibre(
  personnage: PersonnageEnregistre,
  nom: string,
  encombrement: number,
  prix: number | null,
  note: string | null,
): PersonnageEnregistre {
  return ajouter_objet_libre(
    personnage,
    nom,
    encombrement,
    prix ?? undefined,
    note ?? undefined,
  ) as PersonnageEnregistre
}

/** Un Renforcement qu'on peut poser sur un exemplaire, ou pas. */
export interface RenfortOffert {
  id: string
  nom: string
  page: number
  /** Ce qu'il fait, en une ligne. */
  dit: string
  /** Ce qu'il vaut, en valeur absolue. `0` pour une Propriété d'Artisanat. */
  valeur: number
  /** Combien de fois le livre autorise à le poser sur un même objet. */
  quota: number
  /** Ce qui l'empêche, en toutes lettres. `null` : rien ne s'y oppose. */
  refus: string | null
}

/**
 * Une manière de renforcer un objet, et ce qu'elle exige.
 *
 * Le livre en offre trois, dans trois Codex différents : l'établi de
 * l'artisan, le Cercle tracé de l'Alchimiste, et le tatouage de sa paume. Un
 * personnage n'en a le plus souvent aucune.
 */
export interface VoieDeRenfort {
  /** `artisanat`, `cercle` ou `marque`. */
  id: string
  nom: string
  /** Ce que la voie demande : le Test, le matériel, la durée. */
  resume: string
  /** Le personnage peut-il l'emprunter en l'état ? */
  ouverte: boolean
  /** Ce qui lui manque, sinon. */
  manque: string | null
  /** Un tiers peut-il l'emprunter à sa place ? Un Alchimiste de la bande trace
      le Cercle sur l'épée d'un autre ; le Matériel, lui, se consomme au sac. */
  par_un_tiers: boolean
  offres: RenfortOffert[]
}

/**
 * Les Renforcements qu'on peut poser sur un exemplaire.
 *
 * Rien n'est masqué : un Renforcement impossible reste affiché avec sa raison
 * — le quota par objet, une voie que le livre interdit de cumuler, ou un
 * Rituel qui ne dit rien de ce genre d'objet.
 */
export function renfortsPossibles(
  personnage: PersonnageEnregistre,
  objet: string,
): VoieDeRenfort[] {
  return renforts_d_un_objet(personnage, objet) as VoieDeRenfort[]
}

/**
 * Attribue une Propriété à un de nos exemplaires **sans rien consommer**.
 *
 * Le geste de l'allié : un artisan de la bande fait le Test sur notre pièce
 * avec son propre Matériel. Rien ne part de notre sac. Le livre garde son mot
 * sur la pièce elle-même — déjà portée, ou pas de son genre, reste refusé.
 */
export function attribuerUnePropriete(
  personnage: PersonnageEnregistre,
  objet: string,
  propriete: string,
): PersonnageEnregistre {
  return attribuer_une_propriete_wasm(personnage, objet, propriete) as PersonnageEnregistre
}

/**
 * Tente une amélioration d'Artisanat sur un exemplaire.
 *
 * Codex XII p.249 : un Test d'Artisanat Difficile, et **le Matériel part à la
 * tentative, réussie ou non**. Le dé se jette dans le tiroir ; le joueur
 * rapporte le verdict.
 */
export function tenterUneAmelioration(
  personnage: PersonnageEnregistre,
  objet: string,
  propriete: string,
  reussi: boolean,
): PersonnageEnregistre {
  return tenter_une_amelioration_wasm(personnage, objet, propriete, reussi) as PersonnageEnregistre
}

/**
 * Monte une Amélioration de Veredium sur un exemplaire, ou la démonte.
 *
 * Sur l'objet et non sur son porteur : le +3 aux Dégâts d'un Amplificateur
 * appartient à l'arme qui le porte, et disparaît avec elle.
 */
export function basculerAmelioration(
  personnage: PersonnageEnregistre,
  objet: string,
  amelioration: string,
): PersonnageEnregistre {
  return basculer_amelioration_wasm(personnage, objet, amelioration) as PersonnageEnregistre
}

/** Le Matériel d'amélioration qui reste au sac. */
export function materielDAmelioration(personnage: PersonnageEnregistre): number {
  return materiel_d_amelioration_wasm(personnage) as number
}

/**
 * Pose un Renforcement sur un exemplaire, ou l'en retire.
 *
 * `sens` vaut `1` pour octroyer et `-1` pour retirer : le livre écrit
 * « octroyer ou retirer, au choix ». Reposer le même l'enlève.
 */
export function poserUnRenfort(
  personnage: PersonnageEnregistre,
  objet: string,
  source: string,
  sens: number,
): PersonnageEnregistre {
  return poser_un_renfort_wasm(personnage, objet, source, sens) as PersonnageEnregistre
}

/**
 * Donne, ou retire, de l'argent.
 *
 * Le butin d'une séance, la solde d'un contrat, l'amende d'un magistrat : le
 * livre chiffre les prix mais ne dit rien de ce qui remplit une bourse. Jamais
 * en dessous de zéro — le jeu ne connaît pas le crédit.
 */
export function ajusterLaBourse(
  personnage: PersonnageEnregistre,
  delta: number,
): PersonnageEnregistre {
  return ajuster_la_bourse_wasm(personnage, BigInt(delta)) as PersonnageEnregistre
}

/** Se défait d'un objet — jeté, ou vendu à la moitié de son prix. */
export function retirerObjet(
  personnage: PersonnageEnregistre,
  objet: string,
  vendre: boolean,
): PersonnageEnregistre {
  return retirer_objet(personnage, objet, vendre) as PersonnageEnregistre
}

/**
 * Équipe un objet, le range dans le sac, ou le laisse à la maison.
 *
 * Seule une armure équipée protège ; seul ce qu'on emporte pèse. Le geste
 * **pose** l'état au lieu de l'alterner : à trois états, une bascule
 * obligerait à traverser celui dont on ne veut pas.
 */
export function rangerObjet(
  personnage: PersonnageEnregistre,
  objet: string,
  ou: Rangement,
): PersonnageEnregistre {
  return ranger_objet_wasm(personnage, objet, ou) as PersonnageEnregistre
}

/** Un équipement que le jeu a dégradé. */
export interface Abime {
  id: string
  nom: string
  /** Le nom imprimé de l'usure : « Abîmé », « Très abîmé ». */
  usure: string
  /** Ce que l'usure fait, en une phrase du catalogue. */
  resume: string
}

/** Ce que le personnage traîne d'abîmé, arme ou armure. */
export function equipementsAbimes(personnage: PersonnageEnregistre): Abime[] {
  return equipements_abimes(personnage) as Abime[]
}

/** Combien de Matériels de réparation restent dans les contenants portés. */
export function materielDeReparation(personnage: PersonnageEnregistre): number {
  return materiel_de_reparation(personnage)
}

/**
 * Tente une réparation : un Matériel part, et l'objet revient en état si le
 * Test d'Artisanat a réussi.
 *
 * Le livre insiste sur le premier point : chaque tentative consomme un
 * Matériel, réussie ou non (Codex XII p.248).
 */
export function tenterUneReparation(
  personnage: PersonnageEnregistre,
  objet: string,
  reussi: boolean,
): PersonnageEnregistre {
  return tenter_une_reparation(personnage, objet, reussi) as PersonnageEnregistre
}

/** Ce que protège une Localisation, et par quelle pièce. */
export interface Protection {
  localisation: string
  nom: string
  /** Les bornes du 1D100 qui désignent cette zone : sa part des coups. */
  min: number
  max: number
  points: number
  piece: string | null
  /** Le nom de la Propriété qui l'a dégradée — « Abîmé », « Très abîmé ». */
  usure: string | null
}

/**
 * Ce que ce coup coûterait, sans rien appliquer encore.
 *
 * Le livre retranche des Dégâts la Résistance physique et les Points de
 * protection de l'armure portée **à la Localisation touchée** (p.166).
 */
export function apercuEncaissement(
  personnage: PersonnageEnregistre,
  degats: number,
  localisation: string,
): Encaissement {
  return apercu_encaissement(personnage, degats, localisation) as Encaissement
}

/** La silhouette du personnage, pièce par pièce. */
export function protections(personnage: PersonnageEnregistre): Protection[] {
  return protections_du_personnage(personnage) as Protection[]
}

/** La Localisation qu'un 1D100 désigne sur la silhouette. */
export function localisationTouchee(d100: number): string | null {
  return localisation_touchee(d100) as string | null
}

/**
 * Applique un coup encaissé : les PV, la blessure, et l'armure qui s'abîme.
 *
 * Un seul appel plutôt que trois : les trois conséquences s'enchaînent dans le
 * livre, et chacune a sa condition — la blessure suppose une Visée (p.164),
 * l'armure abîmée un coup critique (p.162).
 */
export function subirUnCoup(
  personnage: PersonnageEnregistre,
  subis: number,
  localisation: string,
  critique: boolean,
  visee: boolean,
): PersonnageEnregistre {
  return subir_un_coup(personnage, subis, localisation, critique, visee) as PersonnageEnregistre
}

/** Ouvre une blessure — un coup encaissé, pas une correction à la main. */
export function blesser(
  personnage: PersonnageEnregistre,
  localisation: string,
  severite: string,
): PersonnageEnregistre {
  return blesser_wasm(personnage, localisation, severite) as PersonnageEnregistre
}

// ---------------------------------------------------------------------------
// L'assistant de jet
// ---------------------------------------------------------------------------

/** Qui décide d'appliquer un terme : la fiche, le joueur, le meneur. */
export type Famille = 'fiche' | 'declare' | 'mj'

export interface Candidat {
  id: string
  /** Ce qui produit ce terme : « Statut À terre », « Capacité Ambidextrie ». */
  origine: string
  valeur: number
  /** La clause à laquelle le terme est suspendu, quand il en a une. */
  condition: string | null
  famille: Famille
  /** Coché d'avance — toujours vrai dans la famille `fiche`. */
  actif: boolean
}

/** Une partie du corps qu'on peut viser, et ce que ça coûte. */
export interface Visee {
  localisation: string
  nom: string
  valeur: number
}

/** Un usage de la Compétence, tel que le livre le nomme. */
export interface TypeDeTest {
  id: string
  nom: string
  resume: string
  /** Ce que le meneur aura à jeter en face, quand le livre le précise. */
  oppose: string | null
}

/** Une arme que le personnage peut employer pour ce Test. */
export interface ArmeEnMain {
  id: string
  nom: string
  categorie: string
  categorie_nom: string
  /** Le Niveau de Maîtrise atteint dans la Catégorie. `0` : aucune. */
  maitrise: number
  /** La Catégorie en demande-t-elle une ? Les Armes naturelles, non. */
  maitrise_requise: boolean
  /** Les Dégâts qu'elle inflige à ce personnage-ci, Force de frappe comprise. */
  degats: number | null
  /** Les noms imprimés de ses Propriétés. */
  proprietes: string[]
}

/**
 * Ce que l'écran déclare du Test qu'on s'apprête à jeter.
 *
 * Rien là-dedans ne se devine depuis la fiche : elle ne sait ni si son porteur
 * se bat, ni s'il attaque ou s'il marchande, ni ce qu'il tient. C'est pourtant
 * de quoi dépendent une trentaine de modificateurs du livre.
 */
/** Ce qu'une Compétence vaut une fois appliqué ce que la fiche sait. */
export interface ApercuDeJet {
  competence: string
  /** La Valeur réelle, nue. */
  reelle: number
  /** La somme de ce qui s'applique d'office. Jamais ce qu'on a à déclarer. */
  automatique: number
  /** Ce qu'on jette vraiment, hors Confrontation. */
  effective: number
  /** D'où vient chaque point. */
  termes: string[]
}

/**
 * L'aperçu de toutes les Compétences, en un appel.
 *
 * Seul ce que la fiche sait est compté — Statuts, blessures, Afflictions
 * chiffrées, objets portés. Le tiroir reste l'endroit où l'on déclare le reste.
 */
export function apercuDesJets(personnage: PersonnageEnregistre): ApercuDeJet[] {
  return apercu_des_jets_wasm(personnage) as ApercuDeJet[]
}

export interface Cadre {
  competence: string
  confrontation: boolean
  /** L'usage qu'on fait de la Compétence : `attaque`, `marchandage`… */
  type_de_test: string | null
  /** L'arme employée, quand il y en a une. */
  arme: string | null
  /** La partie du corps visée, s'il y en a une. */
  visee: string | null
}

export interface Assistant {
  competence: string
  nom: string
  /** La Valeur réelle : c'est elle qu'on teste, avant modificateurs. */
  reelle: number
  candidats: Candidat[]
  /** Vide hors des Compétences d'Attaque : on ne vise pas en Éloquence. */
  visees: Visee[]
  /** Les usages qui changent quelque chose pour ce personnage-ci. */
  types: TypeDeTest[]
  /** Celui qui a réellement servi : un type que le cadre ne porte pas revient
      à `null` plutôt que de s'appliquer à moitié. */
  type_retenu: string | null
  /** Ce que le personnage peut avoir en main pour ce Test. */
  armes: ArmeEnMain[]
}

export interface Demande {
  cadre: Cadre
  retenus: string[]
  /** Ce que le meneur ajoute à la main. */
  libre: number
  d100: number
}

/** Ce qu'une Attaque inflige, terme par terme. */
/** Ce qu'une Amélioration ajoute aux Dégâts d'un coup. */
export interface TermeDeDegats {
  origine: string
  valeur: number
  /** La clause du livre. Un terme conditionnel n'entre pas dans le total. */
  condition: string | null
}

export interface DegatsInfliges {
  /** Ce que les Améliorations montées sur l'arme ajoutent. */
  termes: TermeDeDegats[]
  /** Le nom imprimé de l'arme employée. */
  arme: string
  /** Ce que l'arme vaut pour ce personnage, Force de frappe comprise. */
  base: number
  /** Les Degrés de réussite du Test, signés comme le livre les compte. */
  degres: number
  total: number
}

export interface Resolution {
  d100: number
  reelle: number
  difficulte: number
  verdict: 'reussite_critique' | 'reussite' | 'echec' | 'echec_critique'
  reussite: boolean
  critique: boolean
  epique: boolean
  degres: number
  termes: { origine: string; valeur: number }[]
  /** Renseigné pour une Attaque portée avec une arme, `null` ailleurs. */
  degats: DegatsInfliges | null
}

/**
 * Tout ce qui peut peser sur un Test, tel que le cadre le décrit.
 *
 * Le cadre est déclaré par l'écran, jamais deviné : le Malus du cavalier
 * désarçonné suppose une Confrontation, le +10 d'une arme Légère suppose une
 * Attaque, celui de Commerçant un marchandage. La fiche, elle, ne connaît
 * qu'elle-même.
 */
export function assistant(personnage: PersonnageEnregistre, cadre: Cadre): Assistant {
  return assistant_de_jet(personnage, cadre) as Assistant
}

/**
 * Les résultats qu'offre un Jet chanceux, sans doublon.
 *
 * Le livre fait lancer deux D100 et composer un résultat avec la dizaine de
 * l'un et l'unité de l'autre. Le choix entre les compositions revient au
 * joueur : le plus bas passe le mieux, mais un doublé fait un Critique.
 */
export function compositionsChanceuses(premier: number, second: number): number[] {
  return Array.from(compositions_chanceuses(premier, second))
}

/** Ce que l'écran déclare de l'adversaire d'un Test opposé. */
export interface Adverse {
  /** Le profil du bestiaire, quand l'adversaire en a un. */
  profil: string | null
  /** La Compétence opposée — le livre en laisse le choix au meneur. */
  competence: string | null
  /** La Valeur, quand le meneur improvise un adversaire sans profil. */
  valeur: number | null
  modificateur: number
  d100: number
}

/** Le jet d'en face : pas de fiche derrière lui, donc pas de pile à détailler. */
export interface JetAdverse {
  nom: string
  competence: string | null
  valeur: number
  d100: number
  difficulte: number
  verdict: 'reussite_critique' | 'reussite' | 'echec' | 'echec_critique'
  reussite: boolean
  critique: boolean
  degres: number
}

/** L'issue d'un Test opposé, des deux côtés à la fois. */
export interface Opposition {
  joueur: Resolution
  adverse: JetAdverse
  vainqueur: 'joueur' | 'adversaire'
  /** Pourquoi, en toutes lettres — « 3 DR contre 1 », « Réussite critique ». */
  raison: string
}

/** Une créature du bestiaire, telle que le sélecteur l'affiche. */
export interface Adversaire {
  id: string
  nom: string
  section: string
  /** La page du livre, ou `null` pour ce qu'il n'imprime pas. */
  page: number | null
  /** Un Élite tombe en État critique comme un PJ ; les autres meurent à 0 PV. */
  elite: boolean
  pv: number | null
}

export interface CompetenceAdverse {
  id: string
  nom: string
  valeur: number
}

/**
 * Résout un Test opposé.
 *
 * Le livre ne compare que les Degrés, indépendamment de la réussite de chaque
 * Test (Codex II p.33-34) : Hector l'emporte avec 0 DR contre −1 alors que son
 * propre Test est un échec. Les deux dés viennent de l'écran.
 */
export function opposer(
  personnage: PersonnageEnregistre,
  demande: Demande,
  adverse: Adverse,
): Opposition {
  return opposer_un_jet(personnage, demande, adverse) as Opposition
}

/** Les 68 profils du bestiaire, pour choisir qui se dresse en face. */
export function adversaires(): Adversaire[] {
  return adversaires_du_bestiaire() as Adversaire[]
}

/**
 * Ce qu'une créature sait faire, et avec quelle Valeur.
 *
 * Ce que le livre imprime « / » n'y est pas : la créature ne peut pas tenter ce
 * Test du tout, et le proposer serait un piège.
 */
export function competencesAdverses(profil: string): CompetenceAdverse[] {
  return competences_d_un_adversaire(profil) as CompetenceAdverse[]
}

/** Un Niveau de Cercle, et ce qu'il coûte à qui le trace. */
export interface Cercle {
  id: string
  nom: string
  page: number
  /** Le Modificateur au Test de Résilience ésotérique qui ouvre le Rituel. */
  modificateur: number
  /** Ce qu'un Échec inflige à l'Alchimiste. */
  sur_echec: string[]
  ouvert: boolean
  manque: string | null
}

/** Un Rituel, du livre ou de la table. */
export interface Rituel {
  id: string
  nom: string
  cercle: string
  cercle_nom: string
  page: number | null
  duree: string | null
  resume: string
  modificateur: number
  /** Le même, en enchaînant sans laisser passer la durée du précédent. */
  modificateur_enchaine: number
  ouvert: boolean
  /** `null` pour les onze du livre ; l'index sur la fiche pour les autres. */
  index: number | null
}

export interface RituelDeLaMarque {
  id: string
  nom: string
  page: number
  cout: string | null
  effet: string
}

export interface Alchimie {
  cercles: Cercle[]
  rituels: Rituel[]
  marque: RituelDeLaMarque[]
  marque_ouverte: boolean
  atelier: string | null
  bonus_d_atelier: number
  conditions: string
}

/**
 * Ce qu'un Alchimiste peut mener, et ce que cela lui coûte.
 *
 * Trois choses que le livre sépare et que la table cherche ensemble : quel
 * Cercle on sait tracer, ce que le Test coûte, et ce qu'un Échec inflige.
 */
export function alchimie(personnage: PersonnageEnregistre): Alchimie {
  return alchimie_du_personnage(personnage) as Alchimie
}

/**
 * Inscrit un Rituel que le meneur a écrit.
 *
 * Le livre présente ses onze Rituels comme des exemples et dit explicitement
 * que le meneur peut en créer d'autres.
 */
export function ecrireUnRituel(
  personnage: PersonnageEnregistre,
  nom: string,
  cercle: string,
  duree: string | null,
  effet: string,
): PersonnageEnregistre {
  return ecrire_un_rituel_wasm(
    personnage,
    nom,
    cercle,
    duree ?? undefined,
    effet,
  ) as PersonnageEnregistre
}

/** Efface un Rituel de la table. */
export function effacerUnRituel(
  personnage: PersonnageEnregistre,
  index: number,
): PersonnageEnregistre {
  return effacer_un_rituel_wasm(personnage, index) as PersonnageEnregistre
}

/** Un Sort, tel que la carte l'ouvre. */
export interface Sort {
  id: string
  nom: string
  sphere: string
  page: number
  resume: string
  /** L'Élément, pour les seuls Sorts d'Union. */
  element: string | null
  cartes: string[]
}

/** Une Sphère, et ce que la carte tirée y ouvre. */
export interface Sphere {
  id: string
  nom: string
  /** La couleur du jeu : cœur, trèfle, carreau, pique. */
  couleur: string
  page: number
  resume: string
  sorts: Sort[]
}

export interface Magie {
  carte: string
  /** `null` pour le Joker, qui n'a pas de Niveau propre. */
  niveau: string | null
  spheres: Sphere[]
  cartes: string[]
  rachat: { carte: string; prix_pi: number; prix_livres: number } | null
  /** L'ordre compte : Eau puis Foudre double les Dégâts, l'inverse ne fait rien. */
  combinaisons: { premier: string; second: string; effet: string }[]
}

/**
 * Ce qu'une carte ouvre, Sphère par Sphère.
 *
 * Le Cartomancien ne choisit pas son Sort : il tire une carte, et la carte
 * ouvre. Le Joker prend la place de n'importe laquelle sauf l'As.
 */
export function magie(carte: string): Magie {
  return magie_de_la_carte(carte) as Magie
}

/**
 * Ce qu'une carte ouvre **à ce personnage-ci** : sans les Sorts d'Union des
 * éléments qu'il n'a pas choisis. Tant qu'aucun n'est choisi, rien n'est retiré.
 */
export function magieDuPersonnage(personnage: PersonnageEnregistre, carte: string): Magie {
  return magie_du_personnage_wasm(personnage, carte) as Magie
}

/** Une carte du paquet. `niveau` est `null` pour le Joker, qui n'en a pas. */
export interface CarteDuPaquet {
  carte: string
  niveau: string | null
}

/** Un paquet : sa Sphère, ce qui reste à piocher, ce qui est brûlé. */
export interface Paquet {
  sphere: string
  nom: string
  /** `coeur`, `trefle`, `carreau`, `pique`. */
  couleur: string
  /** Porte-t-il un Joker ? */
  favori: boolean
  composition: number
  pioche: CarteDuPaquet[]
  defausse: { carte: string; prix_pi: number; prix_livres: number }[]
}

/**
 * Les paquets du Cartomancien, tels que la fiche les retient.
 *
 * La Défausse persiste d'une séance à l'autre (Codex IX p.210) : c'est elle
 * que la fiche garde. La pioche s'en déduit. C'est le joueur qui pioche, dans
 * son jeu à lui — la fiche retient ce qu'il a tiré.
 */
export interface Grimoire {
  paquets: Paquet[]
  favoris_choisis: number
  favoris_attendus: number
  elements: { id: string; nom: string; choisi: boolean }[]
  elements_choisis: number
  elements_attendus: number
  piochees_du_jour: number
  limite_du_jour: number
  /** D'où vient la limite, en toutes lettres. */
  limite_detail: string
  /** Un Brassard est-il porté ? Sans lui, pas de magie. */
  brassard: boolean
  /** Enfant du Destin ou Volonté transcendante : piocher hors limite. */
  hors_limite_possible: boolean
  pi_disponibles: number
  argent: number | null
}

/**
 * Ce que devient la carte une fois piochée : `defaussee` pour une réussite, un
 * échec ou un Sort auquel on renonce ; `revient` pour une réussite critique ;
 * `echec_critique`, qui la brûle et paralyse le mage.
 */
export type IssueDeSort = 'defaussee' | 'revient' | 'echec_critique'

/** Comment une carte revient de la Défausse. */
export type MoyenDeRachat = 'influence' | 'livres' | 'offerte'

export function grimoire(personnage: PersonnageEnregistre): Grimoire {
  return grimoire_wasm(personnage) as Grimoire
}

export function designerUnFavori(
  personnage: PersonnageEnregistre,
  sphere: string,
): PersonnageEnregistre {
  return designer_un_favori_wasm(personnage, sphere) as PersonnageEnregistre
}

export function choisirUnElement(
  personnage: PersonnageEnregistre,
  element: string,
): PersonnageEnregistre {
  return choisir_un_element_wasm(personnage, element) as PersonnageEnregistre
}

export function jouerUneCarte(
  personnage: PersonnageEnregistre,
  sphere: string,
  carte: string,
  issue: IssueDeSort,
  horsLimite: boolean,
): PersonnageEnregistre {
  return jouer_une_carte_wasm(personnage, sphere, carte, issue, horsLimite) as PersonnageEnregistre
}

export function racheterUneCarte(
  personnage: PersonnageEnregistre,
  sphere: string,
  carte: string,
  moyen: MoyenDeRachat,
): PersonnageEnregistre {
  return racheter_une_carte_wasm(personnage, sphere, carte, moyen) as PersonnageEnregistre
}

export function nouvelleJournee(personnage: PersonnageEnregistre): PersonnageEnregistre {
  return nouvelle_journee_wasm(personnage) as PersonnageEnregistre
}

/** Le Compagnon du Rôdeur, tel que l'écran le montre. */
export interface Compagnon {
  profil: string
  nom: string
  /** La page du livre, ou `null` pour ce qu'il n'imprime pas. */
  page: number | null
  pv: number
  pv_max: number
  vitesse: number | null
  degats: number | null
  /** La Catégorie d'apprivoisement, et ce qu'elle coûte au Test. */
  categorie: string | null
  modificateur: number
  objectif_dr: number
  affinite: number
  affinite_nom: string
  affinite_resume: string
  /** Ce que l'Affinité vaut — les Niveaux se cumulent. */
  affinite_effets: string[]
  ordres: string[]
  reflexes: string[]
  competences: CompetenceAdverse[]
}

/** Un animal qu'on peut apprivoiser. */
export interface Animal {
  id: string
  nom: string
  /** La page du livre, ou `null` pour ce qu'il n'imprime pas. */
  page: number | null
  pv: number | null
  categorie: string | null
  modificateur: number
  objectif_dr: number
}

export interface NiveauDAffinite {
  niveau: number
  nom: string
  page: number
  resume: string
}

export interface VieSauvage {
  compagnon: Compagnon | null
  animaux: Animal[]
  affinites: NiveauDAffinite[]
  /** Ce que le livre donne en exemple : la liste n'est pas fermée. */
  ordres_types: string[]
  reflexes_types: string[]
}

/**
 * Le Compagnon du Rôdeur, et les animaux qu'on peut apprivoiser.
 *
 * Le profil vient du bestiaire — un Loup des bois apprivoisé reste le Loup des
 * bois qu'on peut affronter.
 */
export function vieSauvage(personnage: PersonnageEnregistre): VieSauvage {
  return vie_sauvage_du_personnage(personnage) as VieSauvage
}

/** Apprivoise un animal, ou renvoie celui qu'on avait. */
export function apprivoiser(
  personnage: PersonnageEnregistre,
  profil: string | null,
): PersonnageEnregistre {
  return apprivoiser_wasm(personnage, profil ?? undefined) as PersonnageEnregistre
}

/**
 * Ce que le Compagnon subit, apprend, ou devient.
 *
 * `quoi` vaut `nom`, `pv`, `affinite`, `ordre` ou `reflexe`. Un nombre positif
 * en `pv` compte les Dégâts subis, comme pour une machine.
 */
export function instruireLeCompagnon(
  personnage: PersonnageEnregistre,
  quoi: 'nom' | 'pv' | 'affinite' | 'ordre' | 'reflexe',
  texte: string,
  valeur: number,
): PersonnageEnregistre {
  return instruire_le_compagnon_wasm(personnage, quoi, texte, valeur) as PersonnageEnregistre
}

/** Une Main de Dieu forgée, ou proposée par le livre. */
export interface MainDeDieu {
  /** Le nom de la munition qu'elle consomme. `null` : elle n'en consomme pas. */
  munition: string | null
  /** Ce qu'elle tient au maximum, quand le livre le chiffre. */
  capacite: number | null
  /** Ce qu'elle tient en ce moment. */
  munitions: number
  /** Ce qu'un lot coûte, tel que le livre l'écrit — « 1 PI le lot de 6 ». */
  prix_du_lot: string | null
  prix_faction: number | null
  prix_marche: number | null
  /** Son rang : rien n'interdit d'en forger deux du même Plan. */
  index: number
  nom: string
  /** Le modèle au catalogue, ou `null` quand la table l'a dessinée. */
  modele: string | null
  niveau_de_veredium: number
  /** La Compétence qui la sert — elle appartient à l'arme, pas à sa Catégorie. */
  competence: string | null
  competence_nom: string | null
  /** Les Dégâts de base. `null` : tout passe par le Spécial. */
  degats: number | null
  plus_force_de_frappe: boolean
  portee: string | null
  proprietes: string[]
  special: string | null
  tribut: string | null
  page: number | null
  prix_pi: number | null
}

/** Ce qu'il faut réunir pour forger à ce Niveau de Veredium. */
/**
 * Ce qu'il faut réunir pour forger à ce Niveau — et ce qu'on a.
 *
 * Longtemps la vue n'a porté que la prose du livre, sans la comparer au sac :
 * un Kit acheté pour forger n'était compté nulle part.
 */
export interface NiveauDeForge {
  niveau: number
  page: number
  exigences: string[]
  materiels_requis: number
  /** Kits, Malettes et Valises d'amélioration confondus, usages restants. */
  materiels_possedes: number
  perles_requises: number
  perles_possedees: number
  capacite_requise: string | null
  /** Vrai aussi quand aucune n'est exigée. */
  capacite_acquise: boolean
  test: string | null
  /** Tout est réuni : on peut jeter le Test. */
  forgeable: boolean
}

export interface MainsDeDieu {
  forgees: MainDeDieu[]
  modeles: MainDeDieu[]
  niveaux: NiveauDeForge[]
}

/**
 * Les Mains de Dieu : celles qu'on a forgées, les sept Plans du livre, et ce
 * que chaque Niveau de Veredium exige.
 *
 * Le livre donne sept exemples et la règle de fabrication — rien n'oblige à
 * s'en tenir aux sept.
 */
export function mainsDeDieu(personnage: PersonnageEnregistre): MainsDeDieu {
  return mains_du_personnage(personnage) as MainsDeDieu
}

/**
 * Achète une Main de Dieu toute faite, au prix du livre en Points d'Influence.
 *
 * L'autre voie que la forge, et pas la même affaire : elle coûte plus de
 * Points d'Influence, mais ne consomme ni Perle ni Matériel et ne risque rien
 * sur un dé. Rien ne bouge si la bourse est trop courte.
 */
export function acheterUneMain(
  personnage: PersonnageEnregistre,
  modele: string,
): PersonnageEnregistre {
  return acheter_une_main_wasm(personnage, modele) as PersonnageEnregistre
}

/** Forge une Main de Dieu du livre, sur son Plan. */
export function forgerUneMain(
  personnage: PersonnageEnregistre,
  modele: string,
  reussi: boolean,
): PersonnageEnregistre {
  return forger_une_main_wasm(personnage, modele, reussi) as PersonnageEnregistre
}

/**
 * Inscrit une Main de Dieu que le joueur et le meneur ont dessinée ensemble.
 *
 * Rien n'est deviné : le nom, le Niveau, la Compétence, les Dégâts, le Spécial
 * et le Tribut viennent de la table. L'application la traite ensuite comme les
 * sept du livre — elle se prend en main et se jette pareil.
 */
export function forgerUneMainLibre(
  personnage: PersonnageEnregistre,
  main: {
    nom: string
    niveau: number
    competence: string
    degats: number | null
    plusForceDeFrappe: boolean
    portee: string | null
    special: string | null
    tribut: string | null
  },
  reussi: boolean,
): PersonnageEnregistre {
  return forger_une_main_libre_wasm(
    personnage,
    main.nom,
    main.niveau,
    main.competence,
    main.degats === null ? undefined : BigInt(main.degats),
    main.plusForceDeFrappe,
    main.portee ?? undefined,
    main.special ?? undefined,
    main.tribut ?? undefined,
    reussi,
  ) as PersonnageEnregistre
}

/** Baptise une Main de Dieu, ou lui rend son nom de Plan. */
/**
 * Charge une Main de Dieu, ou en dépense une munition.
 *
 * Le prix ne se décompte pas : le livre vend au lot, en PI ou en livres, et
 * c'est la table qui tranche. Ce que l'application tient, c'est le compte —
 * celui qu'on oublie en séance.
 */
export function chargerUneMain(
  personnage: PersonnageEnregistre,
  index: number,
  delta: number,
): PersonnageEnregistre {
  return charger_une_main_wasm(personnage, index, delta) as PersonnageEnregistre
}

export function renommerUneMain(
  personnage: PersonnageEnregistre,
  index: number,
  nom: string,
): PersonnageEnregistre {
  return renommer_une_main_wasm(personnage, index, nom) as PersonnageEnregistre
}

/** Se défait d'une Main de Dieu. */
export function defaireUneMain(
  personnage: PersonnageEnregistre,
  index: number,
): PersonnageEnregistre {
  return defaire_une_main_wasm(personnage, index) as PersonnageEnregistre
}

/** Un équipement de machine, tel que l'écran le montre. */
export interface EquipementDeMachine {
  id: string
  nom: string
  page: number
  /** `Protection`, `Armement (Arme à feu)`, `Déplacement`… */
  genre: string
  /** Ce que le livre en dit, mot pour mot. */
  effet: string
  protection: number | null
  degats: number | null
  capacite: string | null
  prix_pi: number | null
}

/** Une Capacité spéciale de la créature qu'on pilote, résumé compris. */
export interface CapaciteDeMachine {
  id: string
  nom: string
  /** `passive` ou `active`. */
  type_capacite: string
  resume: string
}

/** Une machine pilotée, avec son état et ce qu'elle porte. */
export interface Machine {
  /** Son rang : rien n'interdit de piloter deux Drones du même modèle. */
  index: number
  modele: string
  nom: string
  /** `drone` ou `meka`. */
  genre: string
  /** La page du livre, ou `null` pour ce qu'il n'imprime pas. */
  page: number | null
  pv: number
  pv_max: number
  vitesse: number | null
  degats: number | null
  /** Le meilleur armement monté, s'il frappe plus fort qu'elle. */
  degats_montes: number | null
  /** Les Blindages ne se cumulent pas : on retient le meilleur. */
  protection: number
  equipements: EquipementDeMachine[]
  montables: EquipementDeMachine[]
  /** Ce que son profil au bestiaire lui donne, résumé compris. */
  capacites: CapaciteDeMachine[]
}

export interface ModeleDeMachine {
  id: string
  nom: string
  genre: string
  /** La page du livre, ou `null` pour ce qu'il n'imprime pas. */
  page: number | null
  pv: number
  prix_pi: number | null
  prix_plan_livres: number | null
}

export interface Machines {
  pilotees: Machine[]
  modeles: ModeleDeMachine[]
}

/**
 * Les panneaux de l'écran Perso que cette fiche a le droit d'ouvrir.
 *
 * Le livre range des mécaniques entières dans le Codex d'une Classe. Un
 * Technodiacre n'a rien à faire avec les Cercles de l'Alchimiste ni avec les
 * cartes du Cartomancien — sauf s'il est entré dans l'ordre, ce que
 * l'historique de Classes dit tout seul.
 */
export interface Domaines {
  machines: boolean
  mains_de_dieu: boolean
  compagnon: boolean
  alchimie: boolean
  magie: boolean
  /** Toujours vrai : une Prothèse s'achète chez un Médecin, pas dans un ordre. */
  prouesse: boolean
}

export function domaines(personnage: PersonnageEnregistre): Domaines {
  return domaines_du_personnage(personnage) as Domaines
}

/**
 * Le garage : ce que le personnage pilote, et ce qu'il pourrait piloter.
 *
 * Les Points de vie d'une machine viennent du bestiaire — c'est le même Drone
 * que celui qu'on peut affronter, et il n'y a aucune raison d'en tenir deux
 * comptes.
 */
export function machines(personnage: PersonnageEnregistre): Machines {
  return machines_du_personnage(personnage) as Machines
}

export function ajouterUneMachine(
  personnage: PersonnageEnregistre,
  modele: string,
): PersonnageEnregistre {
  return ajouter_une_machine_wasm(personnage, modele) as PersonnageEnregistre
}

export function retirerUneMachine(
  personnage: PersonnageEnregistre,
  index: number,
): PersonnageEnregistre {
  return retirer_une_machine_wasm(personnage, index) as PersonnageEnregistre
}

/** Baptise une machine, ou lui rend son nom de modèle. */
export function renommerUneMachine(
  personnage: PersonnageEnregistre,
  index: number,
  nom: string,
): PersonnageEnregistre {
  return renommer_une_machine_wasm(personnage, index, nom) as PersonnageEnregistre
}

/** Encaisse des Dégâts sur une machine, ou en répare. */
export function abimerUneMachine(
  personnage: PersonnageEnregistre,
  index: number,
  subis: number,
): PersonnageEnregistre {
  return abimer_une_machine_wasm(personnage, index, subis) as PersonnageEnregistre
}

/** Boulonne un équipement sur une machine, ou le démonte. */
export function basculerEquipementDeMachine(
  personnage: PersonnageEnregistre,
  index: number,
  equipement: string,
): PersonnageEnregistre {
  return basculer_equipement_de_machine_wasm(personnage, index, equipement) as PersonnageEnregistre
}

/** Un Tribut : ce qu'une greffe coûte, et à quel moment. */
export interface Tribut {
  source: string
  /** `null` : le Tribut est permanent, et pèse sur chaque jet concerné. */
  declencheur: string | null
  dit: string[]
}

/** Une greffe de Prouesse : Prothèse, Amélioration, Main de Dieu. */
export interface Greffe {
  id: string
  nom: string
  /** `prothese`, `amelioration_de_prothese` ou `amelioration_d_equipement`. */
  genre: string
  page: number
  porte_sur: string | null
  prix_livres: number | null
  prix_pi: number | null
  /** Ce qu'elle apporte, en toutes lettres. */
  donne: string[]
  tribut: Tribut | null
}

export interface Prouesse {
  greffes: Greffe[]
  disponibles: Greffe[]
  /** Le récapitulatif de ce qu'on doit, que le livre n'imprime nulle part. */
  tributs: Tribut[]
}

/**
 * Ce que la Prouesse a greffé, ce que ça donne et ce que ça coûte.
 *
 * Le livre imprime chaque Tribut dans une colonne à part, sur trois pages
 * différentes : un Technodiacre qui en cumule trois n'a nulle part le compte
 * de ce qu'il doit.
 */
export function prouesse(personnage: PersonnageEnregistre): Prouesse {
  return prouesse_du_personnage(personnage) as Prouesse
}

/** Greffe, ou retire, une pièce de Prouesse. */
export function basculerGreffe(
  personnage: PersonnageEnregistre,
  greffe: string,
): PersonnageEnregistre {
  return basculer_greffe_wasm(personnage, greffe) as PersonnageEnregistre
}

/** Résout un jet. Le dé, lui, est lancé par l'écran. */
export function resoudre(personnage: PersonnageEnregistre, demande: Demande): Resolution {
  return resoudre_jet(personnage, demande) as Resolution
}

/** Le dé du joueur. C'est ici qu'il est lancé, jamais dans le domaine. */
export function d100(): number {
  return Math.floor(Math.random() * 100) + 1
}

export function feuilleVide(): Feuille {
  return {
    nom: '',
    region: null,
    classe: null,
    caracteristiques: {},
    competences: {},
    capacites: [],
    maitrises: [],
    dotations: [],
  }
}
